import { initializeApp } from "firebase/app"
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from "firebase/firestore"

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
}

console.log("Firebase config:", {
  apiKey: firebaseConfig.apiKey ? "Set" : "Missing",
  authDomain: firebaseConfig.authDomain ? "Set" : "Missing",
  projectId: firebaseConfig.projectId ? "Set" : "Missing",
  storageBucket: firebaseConfig.storageBucket ? "Set" : "Missing",
  messagingSenderId: firebaseConfig.messagingSenderId ? "Set" : "Missing",
  appId: firebaseConfig.appId ? "Set" : "Missing",
  measurementId: firebaseConfig.measurementId ? "Set" : "Missing",
})

const app = initializeApp(firebaseConfig)
export const db = getFirestore(app)
console.log("Firebase initialized successfully")

export interface BlogPost {
  id: string
  title: string
  content: string
  excerpt: string
  category: string
  status: "Published" | "Draft"
  image: string
  slug: string
  readTime: string
  createdAt: Timestamp
  updatedAt: Timestamp
  publishedAt?: Timestamp
}

export interface Category {
  id: string
  name: string
  count: number
  createdAt: Timestamp
  updatedAt: Timestamp
}

// Blog Posts
export async function getBlogPosts(): Promise<BlogPost[]> {
  try {
    const q = query(collection(db, "blogPosts"), where("status", "==", "Published"), orderBy("publishedAt", "desc"))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as BlogPost,
    )
  } catch (error) {
    console.error("Error fetching blog posts:", error)
    return []
  }
}

export async function getBlogPostBySlug(slug: string): Promise<BlogPost | null> {
  try {
    const q = query(
      collection(db, "blogPosts"),
      where("slug", "==", slug),
      where("status", "==", "Published"),
      limit(1),
    )
    const querySnapshot = await getDocs(q)
    if (querySnapshot.empty) return null

    const doc = querySnapshot.docs[0]
    return {
      id: doc.id,
      ...doc.data(),
    } as BlogPost
  } catch (error) {
    console.error("Error fetching blog post:", error)
    return null
  }
}

export async function getAllBlogPosts(): Promise<BlogPost[]> {
  try {
    console.log("Firebase: Fetching all blog posts...")
    const q = query(collection(db, "blogPosts"), orderBy("createdAt", "desc"))
    const querySnapshot = await getDocs(q)
    const posts = querySnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as BlogPost,
    )
    console.log("Firebase: Fetched posts:", posts)
    return posts
  } catch (error) {
    console.error("Error fetching all blog posts:", error)
    return []
  }
}

export async function addBlogPost(post: Omit<BlogPost, "id" | "createdAt" | "updatedAt">): Promise<string> {
  try {
    const now = Timestamp.now()
    const docRef = await addDoc(collection(db, "blogPosts"), {
      ...post,
      createdAt: now,
      updatedAt: now,
      publishedAt: post.status === "Published" ? now : null,
    })
    return docRef.id
  } catch (error) {
    console.error("Error adding blog post:", error)
    throw error
  }
}

export async function updateBlogPost(id: string, updates: Partial<BlogPost>): Promise<void> {
  try {
    const docRef = doc(db, "blogPosts", id)
    const updateData: any = {
      ...updates,
      updatedAt: Timestamp.now(),
    }

    // Set publishedAt when status changes to Published
    if (updates.status === "Published") {
      updateData.publishedAt = Timestamp.now()
    }

    await updateDoc(docRef, updateData)
  } catch (error) {
    console.error("Error updating blog post:", error)
    throw error
  }
}

export async function deleteBlogPost(id: string): Promise<void> {
  try {
    await deleteDoc(doc(db, "blogPosts", id))
  } catch (error) {
    console.error("Error deleting blog post:", error)
    throw error
  }
}

// Categories
export async function getCategories(): Promise<Category[]> {
  try {
    const querySnapshot = await getDocs(collection(db, "categories"))
    return querySnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as Category,
    )
  } catch (error) {
    console.error("Error fetching categories:", error)
    return []
  }
}

export async function getCategoryCount(categoryName: string): Promise<number> {
  try {
    const q = query(
      collection(db, "blogPosts"),
      where("category", "==", categoryName),
      where("status", "==", "Published"),
    )
    const querySnapshot = await getDocs(q)
    return querySnapshot.size
  } catch (error) {
    console.error("Error getting category count:", error)
    return 0
  }
}

export async function addCategory(category: Omit<Category, "id" | "createdAt" | "updatedAt">): Promise<string> {
  try {
    const now = Timestamp.now()
    const docRef = await addDoc(collection(db, "categories"), {
      ...category,
      createdAt: now,
      updatedAt: now,
    })
    return docRef.id
  } catch (error) {
    console.error("Error adding category:", error)
    throw error
  }
}

export async function updateCategory(id: string, updates: Partial<Category>): Promise<void> {
  try {
    const docRef = doc(db, "categories", id)
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    })
  } catch (error) {
    console.error("Error updating category:", error)
    throw error
  }
}

export async function deleteCategory(id: string): Promise<void> {
  try {
    await deleteDoc(doc(db, "categories", id))
  } catch (error) {
    console.error("Error deleting category:", error)
    throw error
  }
}

// Utility function to generate slug
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim()
}

// Utility function to calculate read time
export function calculateReadTime(content: string): string {
  const wordsPerMinute = 200
  const wordCount = content.split(/\s+/).length
  const minutes = Math.ceil(wordCount / wordsPerMinute)
  return `${minutes} min read`
}

// Initialize default categories if they don't exist
export async function initializeDefaultCategories(): Promise<void> {
  try {
    const existing = await getCategories()
    const existingNames = new Set(existing.map((c) => c.name.trim().toLowerCase()))

    const defaultCategories = [
      "Operations & Efficiency",
      "Claims & Fraud",
      "Regulatory Compliance",
      "Scaling & Growth",
      "Leadership & Strategy",
    ]

    for (const name of defaultCategories) {
      const key = name.trim().toLowerCase()
      if (!existingNames.has(key)) {
        await addCategory({ name, count: 0 })
        existingNames.add(key)
      }
    }

    // No console noise in production
  } catch (error) {
    console.error("Error initializing default categories:", error)
  }
}
